// One turn's stream folded into its live state (extracted from useAssistantStream so it can be tested on its own). Pure apart from mutating
// the turn it is given: the hook copies the turn for React after every frame, and calls the host's callbacks (client calls, the surface the
// model saw, a model that did not answer) itself.
//
// The inline-widget contract (17 Oct 2026): `start` gives the saved answer's id up front (the live answer keeps its key to its saved form)
// and says whether the answer places its widgets in its text (`layout: "inline-1"`; every turn streams a start frame, so the frame alone
// says nothing, nor does a block's placement);
// `status: "tool"` means the text so far was preamble — the server discards it, so the live text is cleared too; a block redrawn under its
// id keeps its place; a block's `placement` (inline · progress · tail) is kept on it.
import { INLINE_LAYOUT } from "@ncp/ui/lib/inlineWidgets";
import type { LiveTurn } from "./hooks";
import type { AssistantStreamEvent } from "./types";

/** Applies one frame to the turn. */
export function applyStreamEvent(turn: LiveTurn, ev: AssistantStreamEvent): void {
  switch (ev.type) {
    case "start":
      turn.messageId = ev.messageId;
      // Stated: inline-1 or the earlier layout. Not stated (a server before the field): left open (see isInlineLive).
      if (ev.layout !== undefined) turn.layout = ev.layout === INLINE_LAYOUT ? INLINE_LAYOUT : null;
      break;
    case "token":
      turn.content += ev.text;
      break;
    case "status":
      if (ev.stage === "surface" || ev.stage === "model_unavailable") break;
      // A tool round begins: what the model wrote so far was preamble, which the saved answer will not carry.
      if (ev.stage === "tool") turn.content = "";
      turn.stage = ev.stage;
      turn.stageDetail = ev.detail ?? null;
      break;
    case "tool": {
      const open = ev.phase === "start" ? undefined : [...turn.toolCalls].reverse().find((c) => c.tool === ev.tool && c.phase === "start");
      if (open) {
        open.phase = ev.phase;
        open.summary = ev.summary ?? open.summary;
        open.durationMs = ev.durationMs ?? Date.now() - open.startedAt;
      } else {
        turn.toolCalls.push({ id: `${ev.tool}-${turn.toolCalls.length}`, tool: ev.tool, agentKey: ev.agentKey, phase: ev.phase, summary: ev.summary, durationMs: ev.durationMs, startedAt: Date.now() });
      }
      break;
    }
    case "citation":
      if (!turn.citations.some((c) => c.index === ev.index)) turn.citations.push(ev);
      break;
    case "proposal":
      turn.proposals.push(ev);
      break;
    case "artifact":
      turn.artifacts.push(ev);
      break;
    case "artifact_delta": {
      // A new object each frame, so a pane that reads it re-renders; the text only ever grows until the document is saved.
      const was = turn.writing?.[ev.artifactId];
      turn.writing = { ...(turn.writing ?? {}), [ev.artifactId]: { text: (was?.text ?? "") + (ev.text ?? ""), done: !!ev.done || !!was?.done } };
      break;
    }
    case "confirm":
      turn.confirmations.push(ev);
      break;
    case "delegation":
      turn.delegations.push(ev);
      break;
    case "block": {
      const at = turn.blocks.findIndex((b) => b.id === ev.blockId);
      const placement = ev.placement ?? ev.block.placement ?? (at >= 0 ? turn.blocks[at]!.placement : undefined);
      const block = { ...ev.block, id: ev.blockId, ...(placement ? { placement } : {}) };
      turn.blocks = at >= 0 ? turn.blocks.map((b, i) => (i === at ? block : b)) : [...turn.blocks, block];
      break;
    }
    case "ui": {
      const { type: _type, ...event } = ev;
      turn.ui = [...turn.ui, { ...event, changed: event.changed ?? [] }];
      break;
    }
    case "ask_user": {
      const { type: _type, ...ask } = ev;
      turn.ask = { intro: ask.intro ?? null, questions: ask.questions ?? [] };
      break;
    }
    case "final":
      turn.final = ev;
      turn.messageId = turn.messageId ?? ev.messageId;
      if (turn.content !== ev.content) turn.content = ev.content;
      turn.stage = null;
      break;
    case "error":
      turn.error = ev.message;
      turn.errorCode = ev.code ?? null;
      turn.stage = null;
      break;
    case "client_call":
      break;
  }
}
